import type { V2Balance } from '../types/v2';

/**
 * The order accounts read in, on the Dashboard and on All assets alike:
 * the biggest holding first, measured in USDT.
 *
 * The rule was written out by hand in seven places across the two screens,
 * and one of them had drifted — deposits on the Dashboard were ordered by
 * `updated_at`, so a client with 455 958 USDT, 135 940 and 63 877 read
 * them as 63 877, 455 958, 135 940. One comparator now, so they cannot
 * disagree again.
 */

/**
 * What a balance is worth in USDT, for ordering only.
 *
 * A balance the server could not price has no `value_usd`. Falling back to
 * the raw token amount, as this used to, compares quantities against
 * dollars: a million units of something unpriceable outranks 400 000 USDT.
 * Nothing sensible can be said about where such a holding belongs, so it
 * goes last.
 */
export const usdtValueOf = (balance: Pick<V2Balance, 'value_usd'>): number => {
  const value = parseFloat(balance.value_usd ?? '');
  return Number.isFinite(value) ? value : 0;
};

/** Newest first, for balances worth the same. */
const byUpdatedAt = (a: Pick<V2Balance, 'updated_at'>, b: Pick<V2Balance, 'updated_at'>): number =>
  new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime();

/**
 * Biggest first. Balances worth the same keep a stable order — by the time
 * they were last touched — so a row does not swap places between renders.
 */
export const byUsdtValue = (a: V2Balance, b: V2Balance): number => {
  const diff = usdtValueOf(b) - usdtValueOf(a);
  return diff !== 0 ? diff : byUpdatedAt(a, b);
};

/**
 * The same, with USDT ahead of everything else when the values tie. Used
 * for current accounts, where a client's empty accounts would otherwise
 * order themselves arbitrarily and their primary one is USDT.
 */
export const byUsdtValueUsdtFirst = (a: V2Balance, b: V2Balance): number => {
  const diff = usdtValueOf(b) - usdtValueOf(a);
  if (diff !== 0) { return diff; }
  const aIsUsdt = a.token_symbol?.toUpperCase() === 'USDT' ? -1 : 0;
  const bIsUsdt = b.token_symbol?.toUpperCase() === 'USDT' ? -1 : 0;
  return aIsUsdt !== bIsUsdt ? aIsUsdt - bIsUsdt : byUpdatedAt(a, b);
};
